import { z } from 'zod';
import { type CatalogEntry, defineCatalogEntry, SymbolSchema } from './entry';

/**
 * Research panels of the board catalog — owned by the Research area. Every entry wraps a
 * REAL research surface that takes its scope as simple PROPS and fetches through its own
 * api-query hooks (the cross-hub content design).
 *
 * ZOD-3 COMPATIBILITY: apps/web bundles this SOURCE with zod v3 while the backend runs v4 —
 * only syntax valid in BOTH may be used here.
 *
 * Lifecycle rules (§4.6) — an id is NEVER repurposed (persisted ops reference it forever);
 * a semantic change is a NEW entry; `retired: true` drops an entry from the composer
 * prompt while its FE binding stays so historical boards keep rendering. Props changes are
 * additive (new fields optional/defaulted); a breaking change = new id + retire the old.
 */
export const RESEARCH_PANELS = {
  'earnings-valuation': defineCatalogEntry({
    id: 'earnings-valuation',
    category: 'valuation',
    description:
      'Price vs earnings- or revenue-multiple fair-value projection for one company. Use for "is X expensive/cheap" and valuation checks.',
    props: z.object({
      symbol: SymbolSchema,
      method: z.enum(['earnings', 'revenue']).default('earnings'),
    }),
    adjustable: ['method'],
    breadcrumb: ['Research', 'Projections', 'Earnings valuation'],
    example: { symbol: 'KO.US', method: 'earnings' },
  }),
  'fundamentals-charts': defineCatalogEntry({
    id: 'fundamentals-charts',
    category: 'data',
    description:
      'Historical fundamentals charts (revenue, margins, EPS, debt) for one company. Use when the answer needs the financial track record.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Fundamentals'],
    example: { symbol: 'AAPL.US' },
  }),
  'analyst-views': defineCatalogEntry({
    id: 'analyst-views',
    category: 'data',
    description:
      'Analyst ratings feed and consensus forecasts with price targets for one company. Use for "what do analysts think about X".',
    props: z.object({
      symbol: SymbolSchema,
      view: z.enum(['ratings', 'forecasts']).default('ratings'),
    }),
    adjustable: ['view'],
    breadcrumb: ['Research', 'Projections', 'Analysts'],
    example: { symbol: 'MSFT.US', view: 'ratings' },
  }),
  'quality-check': defineCatalogEntry({
    id: 'quality-check',
    category: 'quality',
    description:
      'The Stoxly quality scoring surface: criteria checks and the composite score for one company. Use for "is X a quality business".',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Quality check'],
    example: { symbol: 'AAPL.US' },
  }),
  'dividends-overview': defineCatalogEntry({
    id: 'dividends-overview',
    category: 'data',
    description:
      'Dividend history, yield, schedule and safety for one company. Use for income and dividend questions.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Dividends'],
    example: { symbol: 'KO.US' },
  }),
  'symbol-news': defineCatalogEntry({
    id: 'symbol-news',
    category: 'news',
    description:
      'The latest news stream for one symbol. Use when recent events or sentiment matter to the answer.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'News'],
    example: { symbol: 'TSLA.US' },
  }),
  'price-chart': defineCatalogEntry({
    id: 'price-chart',
    category: 'data',
    description:
      'Interactive price chart for one stock or ETF over a chosen window, with the change over that window and price/percent/drawdown views. Use for "how has X done over the last year" or "how far is X off its high".',
    props: z.object({
      symbol: SymbolSchema,
      // Mirrors `dateRanges` in apps/web instruments/logic/price-range.ts (pinned by
      // research-panel-enums.spec.ts); `MAX` is the chart's "ALL".
      range: z
        .enum(['1D', '5D', '1M', '3M', '6M', 'YTD', '1Y', '3Y', '5Y', '10Y', 'MAX'])
        .default('1Y'),
    }),
    adjustable: ['range'],
    breadcrumb: ['Research', 'Overview', 'Price chart'],
    example: { symbol: 'AAPL.US', range: '1Y' },
  }),
  'research-overview': defineCatalogEntry({
    id: 'research-overview',
    category: 'data',
    description:
      'The whole research overview page for one symbol: price chart, key stats, company profile, earnings surprises, consensus and headlines (or the fund layout for an ETF). Use for a broad "tell me about X" or "give me a snapshot of X", not for one specific angle.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Overview'],
    example: { symbol: 'NVDA.US' },
  }),
  seasonality: defineCatalogEntry({
    id: 'seasonality',
    category: 'data',
    description:
      'Full seasonality analysis for one symbol: month-by-month return table, the seasonal curve across years and weekday statistics, over its own date window. Use for "is there a seasonal pattern in X" or "which months are strongest for X".',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Projections', 'Seasonality'],
    example: { symbol: 'AAPL.US' },
  }),
  'stock-seasonality': defineCatalogEntry({
    id: 'stock-seasonality',
    category: 'data',
    description:
      'Compact seasonality card: average return per calendar month for one symbol over a 3/5/10/20-year lookback. Use for a quick "how does X usually do in <month>" beside other panels; prefer seasonality for a full analysis.',
    props: z.object({
      symbol: SymbolSchema,
      period: z.enum(['3Y', '5Y', '10Y', '20Y']).default('10Y'),
    }),
    adjustable: ['period'],
    breadcrumb: ['Research', 'Overview', 'Seasonality'],
    example: { symbol: 'MSFT.US', period: '10Y' },
  }),
  'earnings-calls': defineCatalogEntry({
    id: 'earnings-calls',
    category: 'news',
    description:
      'Investor-relations surface for one company: earnings calls, filings and press releases, with the call transcript and its AI analysis. Use for "what did management say on the last call" or "what did X announce to investors".',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Investor relations'],
    example: { symbol: 'AAPL.US' },
  }),
  'fund-holdings': defineCatalogEntry({
    id: 'fund-holdings',
    category: 'data',
    description:
      'The constituents of one ETF or fund: the full holdings table with the weight of each position. Use for "what does <ETF> hold" or "how concentrated is <ETF>". Funds only, never a single stock.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Holdings'],
    example: { symbol: 'SPY.US' },
  }),
  'news-timeline': defineCatalogEntry({
    id: 'news-timeline',
    category: 'news',
    description:
      'Timeline of the key EVENTS for one symbol (earnings, guidance, deals, management changes) over a date window. Use for "what has happened to X this year" or "why did X move"; prefer symbol-news for the latest article stream.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'News', 'Timeline'],
    example: { symbol: 'TSLA.US' },
  }),
  'daily-briefing': defineCatalogEntry({
    id: 'daily-briefing',
    category: 'news',
    description:
      'The AI daily briefing for one symbol: a summary of the day\'s news with sentiment and its sources, browsable by day. Use for "what\'s going on with X today" or "summarize today\'s news on X".',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'News', 'Daily briefing'],
    example: { symbol: 'NVDA.US' },
  }),
  'custom-projections': defineCatalogEntry({
    id: 'custom-projections',
    category: 'valuation',
    description:
      'The DCF-style custom projections model for one company: bull/base/bear revenue, margin and multiple scenarios with the implied price range (view only on a board). Use for "what could X be worth in 5 years" or scenario valuation; prefer earnings-valuation for a quick cheap/expensive check.',
    props: z.object({ symbol: SymbolSchema }),
    adjustable: [],
    breadcrumb: ['Research', 'Projections', 'Custom projections'],
    example: { symbol: 'KO.US' },
  }),
} as const satisfies Record<string, CatalogEntry>;
